import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Divider,
  colors,
} from '@mui/material';
import { ConsumableItem, ConsumableUnitOptions } from 'api/consumablesApi';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useRateForm } from '../useRateForm';
import LoadingSpinner from 'components/Spinners/LoadingSpinner';

interface Props {
  openAddConsumable: boolean;
  handleConsumableClose?: () => void;
  editConsumableItemData?: ConsumableItem | null;
}

function AddOrEditConsumableItems({
  openAddConsumable,
  handleConsumableClose,
  editConsumableItemData,
}: Props) {
  const {
    addConsumableMutate,
    isAddingConsumable,
    updateSingleConsumableItemMutate,
  } = useRateForm(handleConsumableClose);

  const { handleSubmit, control, formState, reset } = useForm({
    defaultValues: editConsumableItemData,
  });

  const handleFormSubmit = async (data: ConsumableItem) => {
    if (editConsumableItemData) {
      await updateSingleConsumableItemMutate({
        id: editConsumableItemData.id,
        data: data,
      });
      reset();
      // Update functionality can be added here in the future
      return;
    }
    await addConsumableMutate({ data: data });
    reset();
  };

  return (
    <Dialog
      open={openAddConsumable}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleConsumableClose();
        }
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        {editConsumableItemData
          ? 'Edit Consumable Item'
          : 'Add Consumable Item'}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <Grid container spacing={2} sx={{ marginTop: '0.1rem' }}>
            <Grid item xs={12}>
              <Controller
                fullWidth
                disableEnforceFocus
                rules={{
                  required: {
                    value: true,
                    message: 'Name is required',
                  },
                }}
                as={
                  <TextField
                    fullWidth
                    label="Name *"
                    value="name"
                    error={!!formState.errors.name}
                    helperText={formState.errors.name?.message}
                  />
                }
                control={control}
                name="name"
                // disabled={!!editConsumableItemData}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="unitType"
                rules={{
                  required: {
                    value: true,
                    message: 'Unit Type is required',
                  },
                }}
                control={control}
                render={(props) => (
                  <Autocomplete
                    fullWidth
                    autoHighlight
                    autoSelect
                    openOnFocus
                    options={ConsumableUnitOptions}
                    getOptionLabel={(option) => String(option)}
                    // disabled={!!editConsumableItemData}
                    // groupBy={(option) => option[0].toUpperCase()}
                    noOptionsText="No matches - Other"
                    value={
                      ConsumableUnitOptions.find(
                        (item) => item === props.value
                      ) || null
                    }
                    onChange={(_, data) => props.onChange(data ?? null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!formState.errors.unitType}
                        helperText={formState.errors.unitType?.message}
                        label="Unit Type *"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
          <DialogActions sx={{ marginTop: '0.8rem' }}>
            <Button
              onClick={handleConsumableClose}
              disabled={isAddingConsumable}
              sx={{ color: colors.grey[600] }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isAddingConsumable}>
              {isAddingConsumable ? <LoadingSpinner size={24} /> : null}
              {editConsumableItemData
                ? 'Update Consumable Item'
                : 'Add Consumable Item'}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddOrEditConsumableItems;
